import {formatFixedNumber, formatPercent} from "../../../../../../shared/numberFormat";
import {
  PDF_PAGE,
  createPainter,
  drawFooter,
  drawHeaderBand,
  drawMetricChips,
  drawSectionHeader,
  getPdfPalette,
  hexToRgb,
} from "../../../../../../shared/pdf/pdfTheme";
import {getDataUrlImageHex, loadTenantLogoImage} from "../../../../../../shared/pdf/pdfTenantLogo";

const {width: PAGE_WIDTH, height: PAGE_HEIGHT, marginX: MARGIN_X, marginBottom: MARGIN_BOTTOM} = PDF_PAGE;
const TABLE_ROW_HEIGHT = 22;
const TABLE_TITLE_HEIGHT = 30;
const TABLE_TOP_PADDING = 14;
const TABLE_BOTTOM_PADDING = 18;
const TABLE_FOOTNOTE_HEIGHT = 24;
const TABLE_SCALE_LABEL_HEIGHT = 18;
const TABLE_AFTER_GAP = 16;
const SECTION_HEADER_HEIGHT = 32;
const AMBIENT_MAX_CARD_HEIGHT = 262;
const AMBIENT_MIN_CARD_HEIGHT = 120;
const AMBIENT_AFTER_GAP = 20;

const collectRings = (geometry) => {
  if (!geometry) {
    return [];
  }
  if (geometry.type === "Polygon") {
    return geometry.coordinates.slice(0, 1);
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.flatMap((polygon) => polygon.slice(0, 1));
  }
  return [];
};

export const getPrescriptionReportTableCardHeight = ({title, rows, totalRow, footnote, topScaleLabel, bottomScaleLabel}) => {
  const headerHeight = title ? TABLE_TITLE_HEIGHT : 0;
  const bodyRows = rows.length + 1 + (totalRow ? 1 : 0);
  const scaleLabelHeight = (topScaleLabel ? TABLE_SCALE_LABEL_HEIGHT : 0)
    + (bottomScaleLabel ? TABLE_SCALE_LABEL_HEIGHT : 0);
  return headerHeight + TABLE_TOP_PADDING + bodyRows * TABLE_ROW_HEIGHT + scaleLabelHeight + TABLE_BOTTOM_PADDING + (footnote ? TABLE_FOOTNOTE_HEIGHT : 0);
};

const getTableOuterHeight = (table) => getPrescriptionReportTableCardHeight(table) + TABLE_AFTER_GAP;

const getAmbientVisual = (ambient, model, fallbackColor) => {
  const features = ambient?.geojson?.features || [];
  const classColors = new Map(model.classRows.map((row) => [Number(row.cls), row.color]));
  const shapes = [];
  const points = [];

  features.forEach((feature) => {
    collectRings(feature.geometry).forEach((ring) => {
      const validRing = ring.filter((point) => Array.isArray(point) && point.length >= 2);
      if (validRing.length < 3) {
        return;
      }
      validRing.forEach(([x, y]) => points.push({x, y}));
      shapes.push({
        color: classColors.get(Number(feature.properties?.cls)) || fallbackColor,
        ring: validRing,
      });
    });
  });

  if (points.length < 3 || shapes.length < 1) {
    return null;
  }

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);

  return {
    bounds: {minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys)},
    shapes,
  };
};

const buildPdf = ({ambient, brandLogo = null, fieldName, landName, model, rowSpacingCm, subject, t}) => {
  const palette = getPdfPalette();
  const pages = [[]];
  const imageRegistry = [];
  let y = 48;

  const commands = () => pages[pages.length - 1];
  const newPage = () => {
    pages.push([]);
    y = 48;
  };
  const ensureSpace = (height) => {
    if (y + height > PAGE_HEIGHT - MARGIN_BOTTOM) {
      newPage();
    }
  };

  const painter = createPainter({
    push: (op) => commands().push(op),
    palette,
    pageWidth: PAGE_WIDTH,
    pageHeight: PAGE_HEIGHT,
  });

  const registerImage = (image) => {
    if (!image?.dataUrl || !image.width || !image.height) {
      return null;
    }
    const hex = getDataUrlImageHex(image.dataUrl);
    if (!hex) {
      return null;
    }
    const name = `Im${imageRegistry.length + 1}`;
    imageRegistry.push({...image, hex, name});
    return name;
  };

  const rgbOps = (color) => hexToRgb(color).map((value) => value.toFixed(3)).join(" ");

  const polygon = (points, fillColor) => {
    if (!Array.isArray(points) || points.length < 3) {
      return;
    }
    const path = points
      .map(([x, top], index) => `${x.toFixed(2)} ${(PAGE_HEIGHT - top).toFixed(2)} ${index === 0 ? "m" : "l"}`)
      .join(" ");
    commands().push(`q ${rgbOps(fillColor)} rg ${path} h f Q`);
  };

  const tableText = (value, x, width, size = 9, options = {}) => {
    painter.text(value, x, y, size, {...options, maxWidth: width});
  };

  // ----- Ambient capture (kept fully vector) ---------------------------------
  const drawAmbientCapture = ({reservedHeight = 0} = {}) => {
    const visual = getAmbientVisual(ambient, model, palette.primary);
    if (!visual) {
      return;
    }

    y = drawSectionHeader(painter, y, t("prescription:creation.pdf.ambient_title", {defaultValue: "Ambientación"}));

    const cardWidth = PAGE_WIDTH - MARGIN_X * 2;
    const availableCardHeight = PAGE_HEIGHT - MARGIN_BOTTOM - y - reservedHeight - AMBIENT_AFTER_GAP;
    const cardHeight = Math.max(
      AMBIENT_MIN_CARD_HEIGHT,
      Math.min(AMBIENT_MAX_CARD_HEIGHT, availableCardHeight)
    );

    painter.roundedRect(MARGIN_X, y, cardWidth, cardHeight, 14, {fill: palette.surfaceAlt, stroke: palette.line, lineWidth: 1});

    const innerPad = 18;
    const visualX = MARGIN_X + innerPad;
    const visualTop = y + innerPad;
    const visualWidth = cardWidth - innerPad * 2;
    const visualHeight = cardHeight - innerPad * 2;
    const contentWidth = Math.max(visual.bounds.maxX - visual.bounds.minX, 0.000001);
    const contentHeight = Math.max(visual.bounds.maxY - visual.bounds.minY, 0.000001);
    const scale = Math.min(visualWidth / contentWidth, visualHeight / contentHeight);
    const drawWidth = contentWidth * scale;
    const drawHeight = contentHeight * scale;
    const offsetX = visualX + (visualWidth - drawWidth) / 2;
    const offsetTop = visualTop + (visualHeight - drawHeight) / 2;

    visual.shapes.forEach((shape) => {
      const transformed = shape.ring.map(([x, coordY]) => [
        offsetX + (x - visual.bounds.minX) * scale,
        offsetTop + (visual.bounds.maxY - coordY) * scale,
      ]);
      polygon(transformed, shape.color);
    });

    y += cardHeight + AMBIENT_AFTER_GAP;
  };

  // ----- Reusable table card -------------------------------------------------
  // headerVariant: "soft" (light header, used for product tables) or
  // "dark" (navy header, used to make the class summary stand out).
  const drawTable = ({
    title,
    columns,
    rows,
    totalRow,
    footnote,
    headerVariant = "soft",
    topScaleLabel = null,
    bottomScaleLabel = null,
  }) => {
    const tableWidth = PAGE_WIDTH - MARGIN_X * 2;
    const rowHeight = TABLE_ROW_HEIGHT;
    const headerHeight = title ? TABLE_TITLE_HEIGHT : 0;
    const cardHeight = getPrescriptionReportTableCardHeight({
      title,
      rows,
      totalRow,
      footnote,
      topScaleLabel,
      bottomScaleLabel,
    });
    const darkHeader = headerVariant === "dark";
    const headerFill = darkHeader ? palette.navy : palette.surfaceAlt;
    const headerText = darkHeader ? palette.onNavy : palette.muted;

    ensureSpace(cardHeight + 10);
    painter.roundedRect(MARGIN_X, y, tableWidth, cardHeight, 14, {
      fill: palette.surface,
      stroke: darkHeader ? palette.navy : palette.line,
      lineWidth: darkHeader ? 1.2 : 1,
    });

    let rowY = y + 18;

    if (title) {
      painter.text(title, MARGIN_X + 18, rowY + 4, 12, {bold: true, color: palette.ink});
      rowY += headerHeight;
    }

    const colX = (index) => MARGIN_X + 18 + columns.slice(0, index).reduce((sum, col) => sum + col.width, 0);

    // header row
    painter.roundedRect(MARGIN_X + 14, rowY - 4, tableWidth - 28, rowHeight, 7, {fill: headerFill});
    columns.forEach((col, index) => {
      const prevY = y;
      y = rowY + 11;
      tableText(col.label, colX(index), col.width - 6, 7.5, {
        bold: true,
        color: headerText,
        letterSpacing: 0.4,
      });
      y = prevY;
    });
    rowY += rowHeight + 4;

    if (topScaleLabel) {
      painter.text(topScaleLabel, MARGIN_X + 18, rowY + 8, 7.5, {
        bold: true,
        color: palette.muted,
        letterSpacing: 0.3,
      });
      rowY += TABLE_SCALE_LABEL_HEIGHT;
    }

    rows.forEach((row, rowIndex) => {
      const prevY = y;
      y = rowY + 9;
      columns.forEach((col, index) => {
        const cellX = colX(index);
        if (col.swatch && row.color) {
          painter.roundedRect(cellX, rowY + 1, 11, 11, 3, {fill: row.color});
          tableText(row[col.key], cellX + 18, col.width - 20, 9, {bold: col.boldValue});
        } else {
          tableText(row[col.key], cellX, col.width - 6, 9, {bold: col.boldValue});
        }
      });
      y = prevY;
      if (rowIndex < rows.length - 1) {
        painter.hline(MARGIN_X + 18, rowY + rowHeight - 3, MARGIN_X + tableWidth - 18, palette.lineSoft, 0.8);
      }
      rowY += rowHeight;
    });

    if (bottomScaleLabel) {
      painter.text(bottomScaleLabel, MARGIN_X + 18, rowY + 8, 7.5, {
        bold: true,
        color: palette.muted,
        letterSpacing: 0.3,
      });
      rowY += TABLE_SCALE_LABEL_HEIGHT;
    }

    if (totalRow) {
      painter.hline(MARGIN_X + 18, rowY - 2, MARGIN_X + tableWidth - 18, palette.line, 1);
      const prevY = y;
      y = rowY + 11;
      columns.forEach((col, index) => {
        tableText(totalRow[col.key] ?? "", colX(index) + (col.swatch ? 18 : 0), col.width - 6, 9, {bold: true, color: palette.ink});
      });
      y = prevY;
      rowY += rowHeight;
    }

    // Footnote lives INSIDE the card, right under the total row.
    if (footnote) {
      painter.text(footnote, MARGIN_X + 18, rowY + 14, 9, {bold: true, color: palette.ink});
    }

    y = y + cardHeight + TABLE_AFTER_GAP;
  };

  const getClassSummaryTable = () => ({
      title: t("prescription:creation.pdf.classes_title", {defaultValue: "Resumen de clases"}),
      headerVariant: "dark",
      topScaleLabel: t("prescription:creation.form.higher_productivity"),
      bottomScaleLabel: t("prescription:creation.form.lower_productivity"),
      columns: [
        {key: "label", label: t("prescription:creation.form.environment"), width: PAGE_WIDTH - MARGIN_X * 2 - 36 - 164, swatch: true, boldValue: true},
        {key: "ha", label: t("prescription:creation.form.surface"), width: 82},
        {key: "percent", label: "%", width: 82},
      ],
      rows: model.classRows.map((row) => ({
        color: row.color,
        label: row.label,
        ha: formatFixedNumber(row.ha),
        percent: formatPercent(row.percent),
      })),
      totalRow: {
        label: t("prescription:creation.form.total"),
        ha: formatFixedNumber(model.totalHa),
        percent: formatPercent(100),
      },
  });

  const drawClassSummary = () => {
    drawTable(getClassSummaryTable());
  };

  const getProductSummaryTable = (summary) => {
    const tableWidth = PAGE_WIDTH - MARGIN_X * 2 - 36;
    const labelWidth = tableWidth - 76 - 56 - 96 - 96;

    return {
      topScaleLabel: t("prescription:creation.form.higher_productivity"),
      bottomScaleLabel: t("prescription:creation.form.lower_productivity"),
      columns: [
        {key: "label", label: t("prescription:creation.form.environment"), width: labelWidth, swatch: true, boldValue: true},
        {key: "ha", label: t("prescription:creation.form.surface"), width: 76},
        {key: "percent", label: "%", width: 56},
        {key: "dose", label: t("prescription:creation.form.dose", {unit: summary.unitLabel}), width: 96},
        {key: "amount", label: t("prescription:creation.form.product_total", {unit: summary.totalUnitLabel}), width: 96},
      ],
      rows: summary.rows.map((row) => ({
        color: row.color,
        label: row.label,
        ha: formatFixedNumber(row.ha),
        percent: formatPercent(row.percent),
        dose: formatFixedNumber(row.value),
        amount: row.total === null ? "-" : formatFixedNumber(row.total),
      })),
      totalRow: {
        label: t("prescription:creation.form.total"),
        ha: formatFixedNumber(summary.totalHa),
        percent: formatPercent(100),
        dose: formatFixedNumber(summary.weightedAverage),
        amount: summary.totalAmount === null ? "-" : formatFixedNumber(summary.totalAmount),
      },
      footnote: `${t("prescription:creation.form.weighted_average")}: ${formatFixedNumber(summary.weightedAverage)} ${summary.unitLabel}`,
    };
  };

  const drawProductSummary = (summary) => {
    const table = getProductSummaryTable(summary);
    ensureSpace(SECTION_HEADER_HEIGHT + getTableOuterHeight(table));
    y = drawSectionHeader(painter, y, t("prescription:creation.form.product_summary_title", {product: summary.name}));
    drawTable(table);
  };

  // ----- Header --------------------------------------------------------------
  const logoName = registerImage(brandLogo);
  const drawLogo = logoName
    ? (currentPainter, {x, top, maxWidth, maxHeight}) => {
        const ratio = Math.min(maxWidth / brandLogo.width, maxHeight / brandLogo.height, 1);
        const width = Math.round(brandLogo.width * ratio);
        const height = Math.round(brandLogo.height * ratio);
        currentPainter.image(logoName, x, top + Math.round((maxHeight - height) / 2), width, height);
      }
    : null;

  y = drawHeaderBand(painter, {
    drawLogo,
    eyebrow: t("prescription:creation.pdf.eyebrow", {defaultValue: "Reporte"}),
    title: t("prescription:creation.pdf.title", {defaultValue: "Reporte de prescripción"}),
  });

  // ----- Key chips -----------------------------------------------------------
  const subjectValue = subject || [fieldName, landName].filter(Boolean).join(" - ");
  const contentWidth = PAGE_WIDTH - MARGIN_X * 2;
  const chips = [
    {
      label: t("prescription:creation.pdf.scope", {defaultValue: "Campo y lote"}),
      value: subjectValue,
      width: model.needsRowSpacing ? contentWidth - 18 - 118 - 145 : contentWidth - 9 - 130,
    },
    {label: t("prescription:creation.form.surface"), value: `${formatFixedNumber(model.totalHa)} ha`, width: model.needsRowSpacing ? 118 : 130},
  ];
  if (model.needsRowSpacing) {
    chips.push({label: t("prescription:creation.form.row_spacing"), value: rowSpacingCm ? `${rowSpacingCm} cm` : "-", width: 145});
  }
  y = drawMetricChips(painter, y, chips);
  const classSummaryTable = getClassSummaryTable();
  drawAmbientCapture({reservedHeight: getTableOuterHeight(classSummaryTable)});
  drawClassSummary();

  newPage();

  if (model.productSummaries.length < 1) {
    ensureSpace(SECTION_HEADER_HEIGHT + 22);
    y = drawSectionHeader(painter, y, t("prescription:creation.pdf.products_title", {defaultValue: "Productos"}));
    painter.text(
      t("prescription:creation.pdf.no_products", {defaultValue: "Todavía no hay columnas de producto completas."}),
      MARGIN_X,
      y,
      10,
      {color: palette.muted}
    );
    y += 22;
  } else {
    model.productSummaries.forEach((summary) => drawProductSummary(summary));
  }

  // ----- Footer --------------------------------------------------------------
  pages.forEach((pageCommands, index) => {
    const pagePainter = createPainter({
      push: (op) => pageCommands.push(op),
      palette,
      pageWidth: PAGE_WIDTH,
      pageHeight: PAGE_HEIGHT,
    });
    drawFooter(pagePainter, {index, total: pages.length});
  });

  // ----- Serialize -----------------------------------------------------------
  const objects = [];
  const setObject = (id, body) => {
    objects[id] = body;
  };
  const nextId = () => objects.length;

  setObject(1, "<< /Type /Catalog /Pages 2 0 R >>");
  setObject(3, "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>");
  setObject(4, "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>");

  imageRegistry.forEach((image) => {
    if (!image.hex) {
      return;
    }
    const imageId = nextId();
    image.objectId = imageId;
    setObject(
      imageId,
      `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${image.hex.length + 1} >>\nstream\n${image.hex}>\nendstream`
    );
  });

  const imageResource = imageRegistry
    .filter((image) => image.objectId)
    .map((image) => `/${image.name} ${image.objectId} 0 R`)
    .join(" ");
  const xObjectResource = imageResource ? `/XObject << ${imageResource} >> ` : "";

  const pageIds = [];
  pages.forEach((pageCommands) => {
    const contentStream = pageCommands.join("\n");
    const contentId = nextId();
    setObject(contentId, `<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`);
    const pageId = nextId();
    setObject(
      pageId,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> ${xObjectResource}>> /Contents ${contentId} 0 R >>`
    );
    pageIds.push(pageId);
  });

  setObject(2, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length}\n`;
  pdf += "0000000000 65535 f \n";
  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return pdf;
};

export const buildPrescriptionReportPdfBlob = async (params) => {
  const palette = getPdfPalette();
  const brandLogo = await loadTenantLogoImage(palette.navy);
  return new Blob([buildPdf({...params, brandLogo})], {type: "application/pdf"});
};

export const buildPrescriptionReportPdf = buildPdf;
